import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { quotationAPI, customerAPI, productAPI, settingsAPI, authAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import logo from '../logo.png';
import { resolveMediaUrl } from '../utils/mediaUrl';

/* ─── Constants ────────────────────────────────────────────────────── */
const QUOTE_TYPES = [
  { value: 'quotation', label: 'Quotation',        desc: 'Price estimate',          icon: '📋' },
  { value: 'proforma',  label: 'Proforma Invoice', desc: 'Pre-delivery estimate',   icon: '📃' },
];
const QUOTE_LABELS = { quotation: 'QUOTATION', proforma: 'PROFORMA INVOICE' };
const DESIGNS = [
  { value: 'classic', label: '📄 Classic', desc: 'Bordered, traditional' },
  { value: 'modern',  label: '🎨 Modern',  desc: 'Teal gradient header' },
  { value: 'minimal', label: '✨ Minimal',  desc: 'Clean, elegant lines' },
];
const UNITS = ['Pcs', 'Nos', 'Kg', 'Gm', 'Ltr', 'Ml', 'Mtr', 'Cm', 'Ft', 'Sqft', 'Box', 'Pack', 'Set', 'Pair', 'Roll', 'Sheet', 'Bag', 'Can', 'Btl', 'Doz'];
const GST_RATES = [0, 3, 5, 12, 18, 28];
const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Credit', 'Online'];
const EMPTY_DUMMY_SHOP_FORM = { shop_name: '', address: '', phone: '', gstin: '', email: '', logo_url: '', design: 'classic' };

/* ─── Utilities ────────────────────────────────────────────────────── */
const fmtDate = d => formatDateDMY(d, '');
const fmtDMY = d => formatDateDMY(d, '');
const fmtNum = v => (parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n = v => parseFloat(v) || 0;
const calcItem = (item, isGST) => {
  const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent), gstPct = n(item.gst_percent);
  const gross = qty * price;
  const discAmt = gross * disc / 100;
  const taxable = gross - discAmt;
  const gstAmt = isGST ? taxable * gstPct / 100 : 0;
  return { ...item, _gross: gross, _discAmt: discAmt, _taxable: taxable, _gstAmt: gstAmt, total: taxable + gstAmt };
};
const numToWords = (num) => {
  if (!num || num === 0) return 'Zero';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const conv = (x) => {
    if (x < 20) return ones[x];
    if (x < 100) return tens[~~(x/10)] + (x%10 ? ' '+ones[x%10] : '');
    if (x < 1000) return ones[~~(x/100)] + ' Hundred' + (x%100 ? ' '+conv(x%100) : '');
    if (x < 100000) return conv(~~(x/1000)) + ' Thousand' + (x%1000 ? ' '+conv(x%1000) : '');
    if (x < 10000000) return conv(~~(x/100000)) + ' Lakh' + (x%100000 ? ' '+conv(x%100000) : '');
    return conv(~~(x/10000000)) + ' Crore' + (x%10000000 ? ' '+conv(x%10000000) : '');
  };
  return conv(Math.round(num));
};
const openPrintWindow = (rootId, pageSize = 'A4') => {
  const node = document.getElementById(rootId);
  if (!node) return;
  const html = node.innerHTML;
  const w = window.open('', '_blank', 'width=940,height=720');
  if (!w) { alert('Pop-up blocked! Allow pop-ups and try again.'); return; }
  w.document.write(`<!DOCTYPE html><html><head>
    <meta charset="utf-8"><title>Quotation</title>
    <style>* { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; background:#fff; color:#000; }
    @page { margin: 8mm; size: ${pageSize}; }
    @media print { html, body { width: 100%; } }
    img { max-width: 100%; }</style>
  </head><body>${html}</body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => { try { w.print(); } catch(e) { console.error('Print failed:', e); } }, 600);
};

/* ─── Design A: Classic Quotation Template ─────────────────────────── */
const QuoteClassic = ({ inv, shop, heading = 'QUOTATION', hideSoftwareFooter = false }) => {
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  const items = inv.items || [];
  const hasHSN = items.some(i => i.hsn_sac);
  const hasDiscount = items.some(i => n(i.discount_percent) > 0);
  const hasGST = items.some(i => n(i.gst_percent) > 0);
  const grandTotal = n(inv.grand_total);
  const subtotal = n(inv.subtotal);
  const gstTotal = n(inv.gst_total);
  const validDate = (() => { const d = new Date(inv.invoice_date || inv.created_at || Date.now()); d.setDate(d.getDate()+30); return fmtDMY(d); })();
  const bdr = '1px solid #000';
  const thS = { border: bdr, padding: '5px 7px', textAlign: 'center', background: '#e8e8e8', fontWeight: 'bold', fontSize: 11 };
  const tdS = { border: bdr, padding: '5px 7px', fontSize: 11 };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#000', background: '#fff', padding: '10mm', boxSizing: 'border-box' }}>
      {/* Title bar */}
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 16, padding: '6px 0', border: bdr, borderBottom: 'none', letterSpacing: 3, textTransform: 'uppercase', background: '#f5f5f5' }}>
        {heading}
      </div>

      {/* Shop + Quotation Details */}
      <div style={{ display: 'flex', border: bdr, borderBottom: 'none' }}>
        <div style={{ flex: 2, padding: '10px 12px', display: 'flex', gap: 12, alignItems: 'flex-start', borderRight: bdr }}>
          <img src={logoSrc} alt="" style={{ width: 60, height: 60, objectFit: 'contain', flexShrink: 0 }}
            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>{shop.shop_name || 'YOUR SHOP'}</div>
            {shop.shop_address && <div style={{ fontSize: 11, lineHeight: 1.6 }}>{shop.shop_address}</div>}
            {shop.shop_phone && <div style={{ fontSize: 11 }}>📞 {shop.shop_phone}</div>}
            {shop.shop_email && <div style={{ fontSize: 11 }}>✉ {shop.shop_email}</div>}
            {shop.gst_number && <div style={{ fontSize: 11, marginTop: 3 }}>GSTIN: <strong>{shop.gst_number}</strong></div>}
          </div>
        </div>
        <div style={{ flex: 1, padding: '10px 12px', fontSize: 12 }}>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr><td style={{ color: '#555', paddingBottom: 3 }}>Quotation No.</td><td style={{ fontWeight: 'bold' }}>{inv.invoice_number}</td></tr>
              <tr><td style={{ color: '#555', paddingBottom: 3 }}>Date</td><td style={{ fontWeight: 'bold' }}>{fmtDMY(inv.invoice_date || inv.created_at)}</td></tr>
              <tr><td style={{ color: '#555', paddingBottom: 3 }}>Valid Until</td><td style={{ fontWeight: 'bold', color: '#c00' }}>{validDate}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bill To */}
      <div style={{ border: bdr, borderBottom: 'none', padding: '8px 12px', minHeight: 60 }}>
        <strong style={{ fontSize: 11 }}>To :</strong>
        {inv.customer_name && <div style={{ fontWeight: 'bold', fontSize: 13, marginTop: 2 }}>{inv.customer_name}</div>}
        {inv.customer_phone && <div style={{ fontSize: 11 }}>📞 {inv.customer_phone}</div>}
        {inv.customer_address && <div style={{ fontSize: 11, lineHeight: 1.5, marginTop: 2 }}>{inv.customer_address}</div>}
      </div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thS, width: 32 }}>S.No.</th>
            <th style={{ ...thS, textAlign: 'left' }}>DESCRIPTION</th>
            {hasHSN && <th style={{ ...thS, width: 70 }}>HSN</th>}
            <th style={{ ...thS, width: 40 }}>UNIT</th>
            <th style={{ ...thS, width: 40 }}>QTY</th>
            <th style={{ ...thS, width: 80 }}>RATE</th>
            {hasDiscount && <th style={{ ...thS, width: 45 }}>DISC%</th>}
            {hasGST && <th style={{ ...thS, width: 40 }}>GST%</th>}
            <th style={{ ...thS, width: 90 }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent);
            const taxable = qty * price * (1 - disc/100);
            const gstAmt = taxable * n(item.gst_percent) / 100;
            const rowTotal = taxable + gstAmt;
            return (
              <tr key={i}>
                <td style={{ ...tdS, textAlign: 'center' }}>{i+1}</td>
                <td style={{ ...tdS }}>
                  <strong>{item.product_name}</strong>
                  {item.description && <div style={{ fontSize: 10, color: '#555', marginTop: 1 }}>{item.description}</div>}
                </td>
                {hasHSN && <td style={{ ...tdS, textAlign: 'center', fontFamily: 'monospace', fontSize: 10 }}>{item.hsn_sac || ''}</td>}
                <td style={{ ...tdS, textAlign: 'center' }}>{item.unit || 'Pcs'}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{qty}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(price)}</td>
                {hasDiscount && <td style={{ ...tdS, textAlign: 'center' }}>{disc > 0 ? `${disc}%` : '-'}</td>}
                {hasGST && <td style={{ ...tdS, textAlign: 'center' }}>{n(item.gst_percent)}%</td>}
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 600 }}>₹ {fmtNum(rowTotal)}</td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 'bold', background: '#f5f5f5' }}>
            <td colSpan={3 + (hasHSN?1:0) + (hasDiscount?1:0) + (hasGST?1:0)} style={{ ...tdS, textAlign: 'right', fontWeight: 'bold' }}>TOTAL</td>
            <td style={{ ...tdS, textAlign: 'right', fontWeight: 'bold' }}>₹ {fmtNum(grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      {/* Grand Total + Terms */}
      <div style={{ display: 'flex', border: bdr, borderTop: 'none' }}>
        <div style={{ flex: 1.5, padding: '8px 12px', borderRight: bdr }}>
          <strong style={{ fontSize: 11 }}>Terms & Conditions:</strong>
          <div style={{ fontSize: 10, marginTop: 4, lineHeight: 1.6, color: '#333' }}>
            {shop.terms_conditions || '1. This is an estimate only. Final prices may vary.\n2. Valid for 30 days from the date of quotation.\n3. Prices are subject to change without notice.\n4. Taxes as applicable.'}
          </div>
          {(shop.bank_name || shop.upi_id) && (
            <div style={{ marginTop: 8, fontSize: 10 }}>
              <strong>Payment Details:</strong>
              {shop.bank_name && <div>Bank: {shop.bank_name} | A/c: {shop.bank_account} | IFSC: {shop.bank_ifsc}</div>}
              {shop.upi_id && <div>UPI: {shop.upi_id}</div>}
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: '8px 12px' }}>
          <table style={{ width: '100%', fontSize: 12 }}>
            <tbody>
              {gstTotal > 0 && <><tr><td>Subtotal</td><td style={{ textAlign: 'right' }}>₹ {fmtNum(subtotal)}</td></tr><tr><td>GST</td><td style={{ textAlign: 'right' }}>₹ {fmtNum(gstTotal)}</td></tr></>}
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #000' }}>
                <td style={{ paddingTop: 4, fontSize: 14 }}>GRAND TOTAL</td>
                <td style={{ textAlign: 'right', paddingTop: 4, fontSize: 14 }}>₹ {fmtNum(grandTotal)}</td>
              </tr>
              <tr><td colSpan={2} style={{ fontSize: 10, color: '#555', paddingTop: 4 }}>Rupees {numToWords(Math.round(grandTotal))} Only</td></tr>
            </tbody>
          </table>
          <div style={{ marginTop: 40, borderTop: '1px solid #000', paddingTop: 6, textAlign: 'center', fontSize: 11 }}>
            {shop.signature_url && (
              <img src={resolveMediaUrl(shop.signature_url)} alt="Signature" style={{ width: 120, height: 38, objectFit: 'contain', marginBottom: 4 }} />
            )}
            Authorised Signatory
          </div>
        </div>
      </div>
      {!hideSoftwareFooter && <div style={{ textAlign: 'center', fontSize: 9, color: '#aaa', marginTop: 6 }}>Powered by Smart Vyapari BillSoft</div>}
    </div>
  );
};

/* ─── Design B: Modern Teal Quotation Template ─────────────────────── */
const QuoteModern = ({ inv, shop, heading = 'QUOTATION', hideSoftwareFooter = false }) => {
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  const items = inv.items || [];
  const hasGST = items.some(i => n(i.gst_percent) > 0);
  const hasDiscount = items.some(i => n(i.discount_percent) > 0);
  const grandTotal = n(inv.grand_total);
  const subtotal = n(inv.subtotal);
  const gstTotal = n(inv.gst_total);
  const validDate = (() => { const d = new Date(inv.invoice_date || inv.created_at || Date.now()); d.setDate(d.getDate()+30); return fmtDate(d); })();
  const teal = '#0d9488'; const tealDark = '#0f766e'; const tealLight = '#f0fdfa';

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", fontSize: 12, color: '#1a1a2e', background: '#fff', padding: 0 }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${teal}, ${tealDark})`, color: '#fff', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={logoSrc} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', background: '#fff', padding: 3, flexShrink: 0 }}
            onError={e => { e.target.onerror = null; e.target.style.display='none'; }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 0.5 }}>{shop.shop_name || 'YOUR SHOP'}</div>
            {shop.gst_number && <div style={{ fontSize: 10, opacity: 0.85, marginTop: 1 }}>GSTIN: {shop.gst_number}</div>}
            {shop.shop_phone && <div style={{ fontSize: 10, opacity: 0.75, marginTop: 1 }}>📞 {shop.shop_phone}  {shop.shop_email ? '✉ '+shop.shop_email : ''}</div>}
            {shop.shop_address && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 1, maxWidth: 280 }}>{shop.shop_address}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 2, color: '#99f6e4' }}>{heading}</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>No: <strong>{inv.invoice_number}</strong></div>
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>Dated: {fmtDate(inv.invoice_date || inv.created_at)}</div>
          <div style={{ fontSize: 11, marginTop: 3, padding: '3px 10px', background: 'rgba(255,255,255,0.2)', borderRadius: 12, display: 'inline-block' }}>
            Valid until: <strong>{validDate}</strong>
          </div>
        </div>
      </div>

      {/* Bill To strip */}
      <div style={{ background: tealLight, padding: '12px 24px', borderBottom: `2px solid ${teal}` }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: tealDark, textTransform: 'uppercase', letterSpacing: 1 }}>Prepared For: </span>
        <strong style={{ fontSize: 14, marginLeft: 6 }}>{inv.customer_name || 'Valued Customer'}</strong>
        {inv.customer_phone && <span style={{ fontSize: 11, color: '#4b5563', marginLeft: 12 }}>📞 {inv.customer_phone}</span>}
        {inv.customer_address && <span style={{ fontSize: 11, color: '#4b5563', marginLeft: 12 }}>📍 {inv.customer_address}</span>}
      </div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#1e293b', color: '#fff' }}>
            <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, width: 30 }}>#</th>
            <th style={{ padding: '9px 8px', textAlign: 'left', fontWeight: 700 }}>Description</th>
            <th style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 700, width: 40 }}>Unit</th>
            <th style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 700, width: 40 }}>Qty</th>
            <th style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 700, width: 80 }}>Rate</th>
            {hasDiscount && <th style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 700, width: 45 }}>Disc%</th>}
            {hasGST && <th style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 700, width: 40 }}>GST</th>}
            <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, width: 88 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent);
            const taxable = qty * price * (1 - disc/100);
            const gstAmt = taxable * n(item.gst_percent) / 100;
            const rowTotal = taxable + gstAmt;
            return (
              <tr key={i} style={{ background: i%2 ? '#f8fffe' : '#fff', borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '8px 12px', color: '#94a3b8', fontSize: 11 }}>{i+1}</td>
                <td style={{ padding: '8px 8px' }}>
                  <div style={{ fontWeight: 600 }}>{item.product_name}</div>
                  {item.description && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{item.description}</div>}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'center', color: '#4b5563' }}>{item.unit || 'Pcs'}</td>
                <td style={{ padding: '8px 8px', textAlign: 'right' }}>{qty}</td>
                <td style={{ padding: '8px 8px', textAlign: 'right' }}>₹{fmtNum(price)}</td>
                {hasDiscount && <td style={{ padding: '8px 8px', textAlign: 'right', color: '#d97706' }}>{disc > 0 ? `${disc}%` : '—'}</td>}
                {hasGST && <td style={{ padding: '8px 8px', textAlign: 'right', color: '#64748b' }}>{n(item.gst_percent)}%</td>}
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: tealDark }}>₹{fmtNum(rowTotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals + Terms */}
      <div style={{ display: 'flex', borderTop: '2px solid #e2e8f0', padding: '16px 24px', justifyContent: 'space-between', gap: 20 }}>
        <div style={{ flex: 1.5, fontSize: 11, color: '#374151', lineHeight: 1.8 }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#111', marginBottom: 4 }}>Terms & Conditions</div>
          <div style={{ whiteSpace: 'pre-line' }}>{shop.terms_conditions || '• This is an estimate only. Prices may vary upon confirmation.\n• Quotation valid for 30 days.\n• Delivery timeline subject to confirmation.\n• All prices exclusive of transport unless stated.'}</div>
          {shop.bank_name && <div style={{ marginTop: 8 }}><strong>Bank:</strong> {shop.bank_name} | A/c: {shop.bank_account} | IFSC: {shop.bank_ifsc}</div>}
          {shop.upi_id && <div><strong>UPI:</strong> {shop.upi_id}</div>}
        </div>
        <div style={{ minWidth: 220 }}>
          {gstTotal > 0 && <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
              <span style={{ color: '#6b7280' }}>Subtotal</span><span>₹{fmtNum(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
              <span style={{ color: '#6b7280' }}>GST</span><span>₹{fmtNum(gstTotal)}</span>
            </div>
          </>}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: teal, color: '#fff', borderRadius: 10, marginTop: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>GRAND TOTAL</span>
            <span style={{ fontWeight: 900, fontSize: 18 }}>₹{fmtNum(grandTotal)}</span>
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 5, textAlign: 'right' }}>Rupees {numToWords(Math.round(grandTotal))} Only</div>
          <div style={{ marginTop: 36, borderTop: `2px solid ${teal}`, paddingTop: 6, textAlign: 'center', fontSize: 11, color: '#374151' }}>
            {shop.signature_url && (
              <img src={resolveMediaUrl(shop.signature_url)} alt="Signature" style={{ width: 120, height: 38, objectFit: 'contain', marginBottom: 4 }} />
            )}
            For <strong>{shop.shop_name}</strong>
            <div style={{ marginTop: 2, color: '#9ca3af', fontSize: 10 }}>Authorised Signatory</div>
          </div>
        </div>
      </div>
      <div style={{ background: `linear-gradient(135deg, ${teal}, ${tealDark})`, padding: '6px 24px', display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>
        <span>This is an estimate — Not a tax invoice</span>
        {!hideSoftwareFooter && <span>Smart Vyapari BillSoft</span>}
      </div>
    </div>
  );
};

/* ─── Design C: Minimal Elegant Quotation Template ──────────────────── */
const QuoteMinimal = ({ inv, shop, heading = 'ESTIMATE', hideSoftwareFooter = false }) => {
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  const items = inv.items || [];
  const hasGST = items.some(i => n(i.gst_percent) > 0);
  const hasDiscount = items.some(i => n(i.discount_percent) > 0);
  const grandTotal = n(inv.grand_total);
  const subtotal = n(inv.subtotal);
  const gstTotal = n(inv.gst_total);
  const validDate = (() => { const d = new Date(inv.invoice_date || inv.created_at || Date.now()); d.setDate(d.getDate()+30); return fmtDate(d); })();

  return (
    <div style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: 12, color: '#111827', background: '#fff', padding: '12mm 14mm', boxSizing: 'border-box', position: 'relative' }}>
      {/* Watermark */}
      <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%) rotate(-30deg)', fontSize: 90, fontWeight: 900, color: 'rgba(0,0,0,0.04)', letterSpacing: 8, userSelect: 'none', zIndex: 0, pointerEvents: 'none', whiteSpace: 'nowrap' }}>{heading}</div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #111827', paddingBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img src={logoSrc} alt="" style={{ width: 52, height: 52, objectFit: 'contain', flexShrink: 0 }}
              onError={e => { e.target.onerror = null; e.target.style.display='none'; }} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>{shop.shop_name || 'YOUR SHOP'}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                {[shop.shop_address, shop.shop_phone ? '📞 '+shop.shop_phone : null, shop.gst_number ? 'GSTIN: '+shop.gst_number : null].filter(Boolean).join('  ·  ')}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 2, color: '#111827' }}>{heading}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>#{inv.invoice_number}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{fmtDate(inv.invoice_date || inv.created_at)}</div>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Valid until {validDate}</div>
          </div>
        </div>

        {/* Bill To */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4 }}>Prepared For</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{inv.customer_name || 'Valued Customer'}</div>
          {inv.customer_phone && <div style={{ fontSize: 11, color: '#4b5563' }}>📞 {inv.customer_phone}</div>}
          {inv.customer_address && <div style={{ fontSize: 11, color: '#4b5563', lineHeight: 1.5, marginTop: 2 }}>{inv.customer_address}</div>}
        </div>

        {/* Items */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #111827' }}>
              <th style={{ padding: '7px 8px 7px 0', textAlign: 'left', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151' }}>Description</th>
              <th style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151', width: 40 }}>Unit</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151', width: 40 }}>Qty</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151', width: 80 }}>Rate</th>
              {hasDiscount && <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151', width: 45 }}>Disc%</th>}
              {hasGST && <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151', width: 40 }}>GST</th>}
              <th style={{ padding: '7px 0 7px 8px', textAlign: 'right', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#374151', width: 90 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent);
              const taxable = qty * price * (1 - disc/100);
              const gstAmt = taxable * n(item.gst_percent) / 100;
              const rowTotal = taxable + gstAmt;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '8px 8px 8px 0', verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 600 }}>{item.product_name}</div>
                    {item.description && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{item.description}</div>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', color: '#4b5563' }}>{item.unit || 'Pcs'}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{qty}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>₹{fmtNum(price)}</td>
                  {hasDiscount && <td style={{ padding: '8px', textAlign: 'right', color: '#d97706' }}>{disc > 0 ? `${disc}%` : '—'}</td>}
                  {hasGST && <td style={{ padding: '8px', textAlign: 'right', color: '#9ca3af' }}>{n(item.gst_percent)}%</td>}
                  <td style={{ padding: '8px 0 8px 8px', textAlign: 'right', fontWeight: 600 }}>₹{fmtNum(rowTotal)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, paddingTop: 12, borderTop: '2px solid #111827' }}>
          <table style={{ width: 240, fontSize: 12 }}>
            <tbody>
              {gstTotal > 0 && <>
                <tr><td style={{ padding: '3px 0', color: '#6b7280' }}>Subtotal</td><td style={{ textAlign: 'right', padding: '3px 0' }}>₹{fmtNum(subtotal)}</td></tr>
                <tr><td style={{ padding: '3px 0', color: '#6b7280' }}>GST</td><td style={{ textAlign: 'right', padding: '3px 0' }}>₹{fmtNum(gstTotal)}</td></tr>
              </>}
              <tr>
                <td style={{ padding: '8px 0 2px', fontWeight: 900, fontSize: 16 }}>Total</td>
                <td style={{ textAlign: 'right', padding: '8px 0 2px', fontWeight: 900, fontSize: 18 }}>₹{fmtNum(grandTotal)}</td>
              </tr>
              <tr><td colSpan={2} style={{ fontSize: 10, color: '#9ca3af' }}>{numToWords(Math.round(grandTotal))} Rupees Only</td></tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: 10, color: '#9ca3af' }}>
          <div style={{ maxWidth: '55%', lineHeight: 1.7 }}>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 3 }}>Notes</div>
            <div>{shop.terms_conditions || 'This is an estimate only. Prices are indicative and subject to change. Kindly confirm before placing the order. Valid for 30 days from the date of estimate.'}</div>
            {(shop.bank_name || shop.upi_id) && <div style={{ marginTop: 6, color: '#4b5563' }}>{shop.bank_name && `Bank: ${shop.bank_name} · A/c: ${shop.bank_account}`} {shop.upi_id && `· UPI: ${shop.upi_id}`}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            {shop.signature_url ? (
              <img src={resolveMediaUrl(shop.signature_url)} alt="Signature" style={{ width: 120, height: 40, objectFit: 'contain', marginBottom: 4 }} />
            ) : (
              <div style={{ height: 40, width: 120, borderBottom: '1px solid #9ca3af', marginBottom: 4 }}></div>
            )}
            <div style={{ color: '#374151', fontWeight: 600, fontSize: 11 }}>{shop.shop_name}</div>
            <div>Authorised Signatory</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Quotation Print Modal ─────────────────────────────────────────── */
const QuotePrintModal = ({ invoice, shop, onClose, dummyShops = [] }) => {
  const [designOverride, setDesignOverride] = useState(null);
  const [headingWord, setHeadingWord] = useState('QUOTATION');
  const [activeShop, setActiveShop] = useState('own'); // 'own' | slot number 1|2|3
  const [tenderMode, setTenderMode] = useState(false);

  const slotMarkupDefaults = { 1: 10, 2: 15, 3: 20 };
  const [markups, setMarkups] = useState(() => {
    const m = {}; dummyShops.forEach(ds => { m[ds.slot] = slotMarkupDefaults[ds.slot] || 10; }); return m;
  });
  const [editingItems, setEditingItems] = useState(() => {
    const e = {}; dummyShops.forEach(ds => { e[ds.slot] = null; }); return e;
  });

  const genRandomInvNo = () => {
    const prefix = ['QT', 'EST', 'QN', 'INV'][Math.floor(Math.random() * 4)];
    const num = Math.floor(1000 + Math.random() * 9000);
    const suffix = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    return `${prefix}-${num}${suffix}`;
  };

  const getSlotDate = (slot) => {
    const base = new Date(invoice.invoice_date || invoice.created_at || Date.now());
    base.setDate(base.getDate() - slot);
    return base.toISOString().slice(0, 10);
  };

  const [slotOverrides, setSlotOverrides] = useState(() => {
    const o = {}; dummyShops.forEach((ds) => {
      o[ds.slot] = { invoice_number: genRandomInvNo(), date: getSlotDate(ds.slot) };
    }); return o;
  });

  const setSlotField = (slot, field, val) => setSlotOverrides(prev => ({ ...prev, [slot]: { ...prev[slot], [field]: val } }));

  const setMarkup = (slot, val) => setMarkups(prev => ({ ...prev, [slot]: val }));

  const getMarkedUpInvoice = (inv, markup) => {
    if (!markup || markup === 0) return inv;
    const factor = 1 + (markup / 100);
    const items = (inv.items || []).map(it => {
      const newPrice = Math.round(n(it.price) * factor * 100) / 100;
      const newTotal = Math.round(n(it.quantity) * newPrice * 100) / 100;
      return { ...it, price: newPrice, total: newTotal };
    });
    const subtotal = items.reduce((s, it) => s + n(it.total), 0);
    const gstTotal = items.reduce((s, it) => s + (n(it.total) * n(it.gst_percent) / (100 + n(it.gst_percent))), 0);
    return { ...inv, items, subtotal, gst_total: gstTotal, grand_total: subtotal };
  };

  const getEditedInvoice = (slot) => {
    const edited = editingItems[slot];
    let result;
    if (!edited) {
      result = getMarkedUpInvoice(invoice, markups[slot] || 0);
    } else {
      const items = edited;
      const subtotal = items.reduce((s, it) => s + n(it.total), 0);
      const gstTotal = items.reduce((s, it) => s + (n(it.total) * n(it.gst_percent) / (100 + n(it.gst_percent))), 0);
      result = { ...invoice, items, subtotal, gst_total: gstTotal, grand_total: subtotal };
    }
    const overrides = slotOverrides[slot] || {};
    if (overrides.invoice_number) result = { ...result, invoice_number: overrides.invoice_number };
    if (overrides.date) result = { ...result, created_at: overrides.date };
    return result;
  };

  const startEditing = (slot) => {
    const base = getMarkedUpInvoice(invoice, markups[slot] || 0);
    setEditingItems(prev => ({ ...prev, [slot]: (base.items || []).map(it => ({ ...it })) }));
  };

  const updateEditItem = (slot, idx, field, val) => {
    setEditingItems(prev => {
      const items = [...(prev[slot] || [])];
      items[idx] = { ...items[idx], [field]: val };
      if (field === 'price' || field === 'quantity') {
        items[idx].total = Math.round(n(items[idx].quantity) * n(items[idx].price) * 100) / 100;
      }
      return { ...prev, [slot]: items };
    });
  };

  const stripRealShopDetails = (shopObj) => ({
    ...shopObj, bank_name: null, bank_account: null, bank_ifsc: null, upi_id: null, terms_conditions: null
  });

  const getDesign = () => {
    if (activeShop === 'own') return designOverride || 'classic';
    const ds = dummyShops.find(d => String(d.slot) === String(activeShop));
    return designOverride || ds?.design || 'classic';
  };
  const getShopInfo = () => {
    if (activeShop === 'own') return shop;
    const ds = dummyShops.find(d => String(d.slot) === String(activeShop));
    if (!ds) return shop;
    return stripRealShopDetails({ ...shop, shop_name: ds.shop_name, shop_address: ds.address || '', shop_phone: ds.phone || '', shop_email: ds.email || '', gst_number: ds.gstin || '', logo_url: ds.logo_url || null });
  };

  const design = getDesign();
  const shopInfo = getShopInfo();
  const Template = design === 'modern' ? QuoteModern : design === 'minimal' ? QuoteMinimal : QuoteClassic;
  const printId = `q-print-root-${activeShop}`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 10 }}>
      <div style={{ background: '#f1f5f9', borderRadius: 14, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', marginBottom: 20, maxHeight: '98dvh', overflowY: 'auto' }} className="animate-scaleIn">
        {/* Header */}
        <div style={{ background: '#1e293b', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>🖨️ Print Quotation — {invoice.invoice_number}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '14px 20px', display: 'flex', gap: 14, flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0', alignItems: 'flex-start' }}>
          {/* Shop selector */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Print As</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button onClick={() => setActiveShop('own')}
                style={{ padding: '7px 12px', borderRadius: 7, border: `2px solid ${activeShop === 'own' ? '#3b82f6' : '#cbd5e1'}`, background: activeShop === 'own' ? '#eff6ff' : '#fff', cursor: 'pointer', textAlign: 'left', fontSize: 12, fontWeight: activeShop === 'own' ? 700 : 400 }}>
                🏪 My Shop — {shop.shop_name}
              </button>
              {dummyShops.map(ds => (
                <button key={ds.slot} onClick={() => setActiveShop(ds.slot)}
                  style={{ padding: '7px 12px', borderRadius: 7, border: `2px solid ${activeShop === ds.slot ? '#3b82f6' : '#cbd5e1'}`, background: activeShop === ds.slot ? '#eff6ff' : '#fff', cursor: 'pointer', textAlign: 'left', fontSize: 12, fontWeight: activeShop === ds.slot ? 700 : 400 }}>
                  🏭 Slot {ds.slot} · {ds.shop_name} <span style={{ fontSize: 10, color: '#64748b' }}>({ds.design})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Design + Wording */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Design</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DESIGNS.map(d => (
                <button key={d.value} onClick={() => setDesignOverride(d.value)}
                  style={{ padding: '6px 10px', borderRadius: 7, border: `2px solid ${design === d.value ? '#0d9488' : '#cbd5e1'}`, background: design === d.value ? '#f0fdfa' : '#fff', cursor: 'pointer', fontSize: 11, fontWeight: design === d.value ? 700 : 400 }}>
                  {d.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, marginTop: 10, textTransform: 'uppercase' }}>Heading Word</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['QUOTATION', 'ESTIMATE', 'PROFORMA'].map(w => (
                <button key={w} onClick={() => setHeadingWord(w)}
                  style={{ padding: '5px 10px', borderRadius: 6, border: `2px solid ${headingWord === w ? '#7c3aed' : '#cbd5e1'}`, background: headingWord === w ? '#f5f3ff' : '#fff', cursor: 'pointer', fontSize: 11, fontWeight: headingWord === w ? 700 : 400 }}>
                  {w}
                </button>
              ))}
            </div>
          </div>

          {/* Tender mode toggle */}
          {dummyShops.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Tender Set</div>
              <button onClick={() => setTenderMode(!tenderMode)}
                style={{ padding: '8px 14px', borderRadius: 7, background: tenderMode ? '#7c3aed' : '#f1f5f9', color: tenderMode ? '#fff' : '#374151', border: tenderMode ? 'none' : '1px solid #cbd5e1', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                {tenderMode ? '✓ Tender Mode ON' : '📋 Generate Tender Set'}
              </button>
              {tenderMode && <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 4 }}>Shows all dummy shops · prices editable below</div>}
            </div>
          )}
        </div>

        {/* Price Markup Controls — shown in tender mode */}
        {tenderMode && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', background: '#fefce8' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', marginBottom: 8, textTransform: 'uppercase' }}>💰 Price Markup Per Slot (makes tender prices higher than original)</div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {dummyShops.map(ds => (
                <div key={ds.slot} style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Slot {ds.slot} · {ds.shop_name}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="number" min="0" max="500" step="1" value={markups[ds.slot] || 0}
                      onChange={e => { setMarkup(ds.slot, parseFloat(e.target.value) || 0); setEditingItems(prev => ({ ...prev, [ds.slot]: null })); }}
                      style={{ width: 60, padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700, textAlign: 'center' }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>%</span>
                    <input type="text" placeholder="Invoice No." value={(slotOverrides[ds.slot] || {}).invoice_number || ''}
                      onChange={e => setSlotField(ds.slot, 'invoice_number', e.target.value)}
                      style={{ width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11 }} />
                    <StrictDateInput value={(slotOverrides[ds.slot] || {}).date || ''}
                      onChange={e => setSlotField(ds.slot, 'date', e.target.value)}
                      style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11 }} />
                    <button onClick={() => startEditing(ds.slot)}
                      style={{ padding: '5px 10px', borderRadius: 6, background: editingItems[ds.slot] ? '#7c3aed' : '#f1f5f9', color: editingItems[ds.slot] ? '#fff' : '#374151', border: '1px solid #cbd5e1', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                      {editingItems[ds.slot] ? '✏️ Editing' : '✏️ Edit Items'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ padding: '10px 20px', display: 'flex', gap: 10, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          {!tenderMode ? (
            <button onClick={() => openPrintWindow(printId)}
              style={{ padding: '8px 22px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              🖨️ Print
            </button>
          ) : (
            <>
              {dummyShops.map(ds => (
                <button key={ds.slot} onClick={() => openPrintWindow(`q-tender-${ds.slot}`)}
                  style={{ padding: '6px 14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                  Print Slot {ds.slot}
                </button>
              ))}
            </>
          )}
          <button onClick={onClose} style={{ padding: '8px 18px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Close</button>
        </div>

        {/* Preview Area */}
        {!tenderMode ? (
          <div id={printId} style={{ background: '#fff', margin: 16, borderRadius: 8, overflowX: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
            <Template inv={activeShop === 'own' ? invoice : getMarkedUpInvoice(invoice, 0)} shop={shopInfo} heading={headingWord} hideSoftwareFooter={activeShop !== 'own'} />
          </div>
        ) : (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {dummyShops.map(ds => {
              const dsInfo = stripRealShopDetails({ ...shop, shop_name: ds.shop_name, shop_address: ds.address || '', shop_phone: ds.phone || '', shop_email: ds.email || '', gst_number: ds.gstin || '', logo_url: ds.logo_url || null });
              const TSlot = ds.design === 'modern' ? QuoteModern : ds.design === 'minimal' ? QuoteMinimal : QuoteClassic;
              const slotInv = getEditedInvoice(ds.slot);
              const isEditing = !!editingItems[ds.slot];
              return (
                <div key={ds.slot}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Slot {ds.slot} — {ds.shop_name} <span style={{ fontWeight: 400, color: '#64748b' }}>({ds.design}) | +{markups[ds.slot] || 0}% markup | Grand Total: ₹{fmtNum(slotInv.grand_total)}</span></span>
                  </div>
                  {/* Inline item editor */}
                  {isEditing && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8, overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead><tr style={{ background: '#e2e8f0' }}>
                          <th style={{ padding: '4px 8px', textAlign: 'left' }}>#</th>
                          <th style={{ padding: '4px 8px', textAlign: 'left' }}>Product</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right', width: 70 }}>Qty</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right', width: 90 }}>Price</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right', width: 90 }}>Total</th>
                        </tr></thead>
                        <tbody>{(editingItems[ds.slot] || []).map((it, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '4px 8px' }}>{idx + 1}</td>
                            <td style={{ padding: '4px 8px' }}>{it.product_name}</td>
                            <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                              <input type="number" value={it.quantity} onChange={e => updateEditItem(ds.slot, idx, 'quantity', e.target.value)}
                                style={{ width: 50, padding: '2px 4px', borderRadius: 4, border: '1px solid #cbd5e1', textAlign: 'right', fontSize: 11 }} />
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                              <input type="number" value={it.price} onChange={e => updateEditItem(ds.slot, idx, 'price', e.target.value)}
                                style={{ width: 70, padding: '2px 4px', borderRadius: 4, border: '1px solid #cbd5e1', textAlign: 'right', fontSize: 11 }} />
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>₹{fmtNum(it.total)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ overflowX: 'auto', borderRadius: 6 }}>
                    <div id={`q-tender-${ds.slot}`} style={{ background: '#fff', borderRadius: 6, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transform: 'scale(0.75)', transformOrigin: 'top left', width: '133%' }}>
                      <TSlot inv={slotInv} shop={dsInfo} heading={headingWord} hideSoftwareFooter={true} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Quotation List ────────────────────────────────────────────────── */
const QuotationList = ({ onCreateNew, onPrint, onEdit, darkMode, color }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteErr, setDeleteErr] = useState('');
  const [delPw, setDelPw] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const searchRef = useRef(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quotationAPI.list({ page, limit: 15, search: search || undefined });
      setItems(res.data.quotations || res.data.invoices || []);
      setTotal(res.data.total || 0);
    } catch (error) { console.error('Failed to load quotations:', error); setLoading(false); }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const inInput = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable;

      if (e.key === 'Escape') {
        if (deletingId) { setDeletingId(null); setDelPw(''); setDeleteErr(''); }
        else if (selectedId) setSelectedId(null);
        return;
      }
      if (inInput) return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); onCreateNew(); return; }
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); return; }
      if (items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = items.findIndex(i => i.id === selectedId);
        const next = idx < items.length - 1 ? idx + 1 : 0;
        setSelectedId(items[next].id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = items.findIndex(i => i.id === selectedId);
        const prev = idx > 0 ? idx - 1 : items.length - 1;
        setSelectedId(items[prev].id);
      } else if (e.key === 'Enter' && selectedId) {
        e.preventDefault();
        const sel = items.find(i => i.id === selectedId);
        if (sel) onEdit(sel);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, selectedId, deletingId, onCreateNew, onEdit]);

  const handleDeleteConfirmed = async () => {
    if (!delPw.trim()) { setDeleteErr('Enter your account password'); return; }
    setDelBusy(true); setDeleteErr('');
    try {
      await authAPI.verifyPassword(delPw);
      await quotationAPI.delete(deletingId);
      setDeletingId(null); setDelPw(''); setSelectedId(null); load();
    } catch (e) {
      setDeleteErr(e.response?.data?.detail || 'Incorrect password');
    }
    setDelBusy(false);
  };

  const selectedItem = items.find(i => i.id === selectedId);
  const totalPages = Math.ceil(total / 15);
  const typeBadge = t => {
    const labels = { quotation: 'Quote', proforma: 'Pro-F' };
    return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#2d3f26' : '#dcfce7', color: '#15803d' }}>{labels[t] || t}</span>;
  };

  return (
    <>
      <div style={{ ...card, padding: isMobile ? 10 : 14, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ ...inp, flex: 1, minWidth: isMobile ? 120 : 180, width: isMobile ? '100%' : undefined }} placeholder="Search quotation# or customer…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} ref={searchRef} />
        <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : undefined }}>
          <button onClick={() => navigate('/billing')} style={{ flex: isMobile ? 1 : undefined, padding: '8px 12px', background: darkMode ? '#1E293B' : '#fff8ed', color: '#d97706', border: `1px solid ${darkMode ? '#d97706' : '#fde68a'}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
            💰 Billing
          </button>
          <button onClick={onCreateNew} style={{ flex: isMobile ? 1 : undefined, padding: '8px 14px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
            ➕ New
          </button>
        </div>
      </div>

      <div style={{ ...card, overflow: 'auto' }} className="stagger-list">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50, ...muted }}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>📋</div>
            <p style={{ ...muted, marginBottom: 14 }}>No quotations found</p>
            <button onClick={onCreateNew} style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Create First Quotation</button>
          </div>
        ) : isMobile ? (
          /* ─── Mobile Card Layout ─── */
          <div style={{ padding: 8 }}>
            {items.map(inv => (
              <div key={inv.id} onClick={() => setSelectedId(inv.id === selectedId ? null : inv.id)}
                style={{ padding: 12, marginBottom: 8, borderRadius: 10, border: `1px solid ${inv.id === selectedId ? acc : (darkMode ? '#334155' : '#E2E8F0')}`, background: inv.id === selectedId ? (darkMode ? '#1e3a5f' : '#eff6ff') : (darkMode ? '#0F172A' : '#FAFBFC'), cursor: 'pointer', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: acc }}>{inv.invoice_number}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, ...pg, wordBreak: 'break-word', marginTop: 2 }}>{inv.customer_name}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, ...pg }}>₹{fmtNum(inv.grand_total)}</div>
                    {typeBadge(inv.bill_type)}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <span style={{ fontSize: 11, ...muted }}>{fmtDate(inv.invoice_date || inv.created_at)}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={e => { e.stopPropagation(); onPrint(inv); }} style={{ padding: '4px 10px', background: darkMode ? '#1E293B' : '#f0fdf4', color: '#16a34a', border: `1px solid ${darkMode ? '#16a34a' : '#bbf7d0'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🖨️</button>
                    <button onClick={e => { e.stopPropagation(); onEdit(inv); }} style={{ padding: '4px 10px', background: darkMode ? '#1E293B' : '#eff6ff', color: '#3b82f6', border: `1px solid ${darkMode ? '#3b82f6' : '#bfdbfe'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                    <button onClick={e => { e.stopPropagation(); setDeletingId(inv.id); }} style={{ padding: '4px 10px', background: darkMode ? '#1E293B' : '#fff1f2', color: '#dc2626', border: `1px solid ${darkMode ? '#dc2626' : '#fecaca'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                {['Quot.#', 'Type', 'Customer', 'Date', 'Amount', ''].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: h === '' ? 'right' : 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(inv => {
                const isSel = inv.id === selectedId;
                return (
                  <tr key={inv.id} onClick={() => setSelectedId(isSel ? null : inv.id)}
                    style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, cursor: 'pointer', background: isSel ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', outline: isSel ? `2px solid ${acc}` : 'none', outlineOffset: -1, transition: 'background 0.12s' }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: acc }}>{inv.invoice_number}</td>
                    <td style={{ padding: '10px 12px' }}>{typeBadge(inv.bill_type)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, ...pg }}>{inv.customer_name}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, ...muted }}>{fmtDate(inv.invoice_date || inv.created_at)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, ...pg }}>₹{fmtNum(inv.grand_total)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <button onClick={e => { e.stopPropagation(); onPrint(inv); }} style={{ marginRight: 6, padding: '5px 12px', background: darkMode ? '#1E293B' : '#f0fdf4', color: '#16a34a', border: `1px solid ${darkMode ? '#16a34a' : '#bbf7d0'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>🖨️</button>
                      <button onClick={e => { e.stopPropagation(); onEdit(inv); }} style={{ marginRight: 6, padding: '5px 12px', background: darkMode ? '#1E293B' : '#eff6ff', color: '#3b82f6', border: `1px solid ${darkMode ? '#3b82f6' : '#bfdbfe'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✏️</button>
                      <button onClick={e => { e.stopPropagation(); setDeletingId(inv.id); }} style={{ padding: '5px 12px', background: darkMode ? '#1E293B' : '#fff1f2', color: '#dc2626', border: `1px solid ${darkMode ? '#dc2626' : '#fecaca'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div style={{ padding: '12px 18px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button disabled={page === 1} onClick={() => setPage(p => p-1)} style={{ padding: '5px 14px', background: 'transparent', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, cursor: 'pointer', ...pg, fontWeight: 600 }}>‹ Prev</button>
            <span style={{ ...muted, fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p+1)} style={{ padding: '5px 14px', background: 'transparent', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, cursor: 'pointer', ...pg, fontWeight: 600 }}>Next ›</button>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {deletingId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) { setDeletingId(null); setDelPw(''); setDeleteErr(''); } }}>
          <div style={{ background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 14, padding: 28, maxWidth: 'min(1120px, calc(100vw - 12px))', width: '100%', maxHeight: '98dvh', overflowY: 'auto' }} className="animate-scaleIn">
            <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>🗑️</div>
            <h3 style={{ ...pg, textAlign: 'center', marginBottom: 6, fontSize: 17 }}>Delete Quotation?</h3>
            <p style={{ ...muted, textAlign: 'center', fontSize: 13, marginBottom: 16 }}>Enter your account password to confirm.</p>
            <input type="password" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} placeholder="Account password"
              value={delPw} onChange={e => { setDelPw(e.target.value); setDeleteErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleDeleteConfirmed()} />
            {deleteErr && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>⚠ {deleteErr}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => { setDeletingId(null); setDelPw(''); setDeleteErr(''); }} style={{ flex: 1, padding: 10, background: 'transparent', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, cursor: 'pointer', ...pg, fontWeight: 600 }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} disabled={delBusy} style={{ flex: 1, padding: 10, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: delBusy ? 'not-allowed' : 'pointer' }}>{delBusy ? '⏳...' : '🗑️ Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Dummy Shops Manager ───────────────────────────────────────────── */
const DummyShopsManager = ({ darkMode, color }) => {
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const [shops, setShops] = useState([null, null, null]); // index 0=slot1, 1=slot2, 2=slot3
  const [forms, setForms] = useState([{ ...EMPTY_DUMMY_SHOP_FORM }, { ...EMPTY_DUMMY_SHOP_FORM, design: 'modern' }, { ...EMPTY_DUMMY_SHOP_FORM, design: 'minimal' }]);
  const [saving, setSaving] = useState([false, false, false]);
  const [msg, setMsg] = useState(['', '', '']);
  const [logoUploading, setLogoUploading] = useState([false, false, false]);
  const [loading, setLoading] = useState(true);
  const msgTimers = useRef([]);
  const logoInputRefs = useRef([null, null, null]);

  useEffect(() => {
    const timers = msgTimers.current;
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  useEffect(() => {
    quotationAPI.listDummyShops().then(res => {
      const newForms = [{ ...EMPTY_DUMMY_SHOP_FORM }, { ...EMPTY_DUMMY_SHOP_FORM, design: 'modern' }, { ...EMPTY_DUMMY_SHOP_FORM, design: 'minimal' }];
      const newShops = [null, null, null];
      (res.data || []).forEach(s => {
        const idx = s.slot - 1;
        if (idx >= 0 && idx < 3) {
          newShops[idx] = s;
          newForms[idx] = { shop_name: s.shop_name, address: s.address || '', phone: s.phone || '', gstin: s.gstin || '', email: s.email || '', logo_url: s.logo_url || '', design: s.design || (idx === 1 ? 'modern' : idx === 2 ? 'minimal' : 'classic') };
        }
      });
      setShops(newShops);
      setForms(newForms);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setField = (slotIdx, field, val) => setForms(prev => prev.map((f, i) => i === slotIdx ? { ...f, [field]: val } : f));

  const save = async (slotIdx) => {
    const f = forms[slotIdx];
    if (!f.shop_name.trim()) { setMsg(prev => prev.map((m, i) => i === slotIdx ? '⚠️ Shop name is required' : m)); return; }
    setSaving(prev => prev.map((v, i) => i === slotIdx ? true : v));
    try {
      const res = await quotationAPI.saveDummyShop(slotIdx + 1, f);
      setShops(prev => prev.map((s, i) => i === slotIdx ? res.data : s));
      setMsg(prev => prev.map((m, i) => i === slotIdx ? '✅ Saved!' : m));
      const tid = setTimeout(() => setMsg(prev => prev.map((m, i) => i === slotIdx ? '' : m)), 3000);
      msgTimers.current.push(tid);
    } catch (e) { setMsg(prev => prev.map((m, i) => i === slotIdx ? '❌ ' + (e.response?.data?.detail || 'Save failed') : m)); }
    setSaving(prev => prev.map((v, i) => i === slotIdx ? false : v));
  };

  const clear = async (slotIdx) => {
    if (!window.confirm(`Clear Slot ${slotIdx + 1}?`)) return;
    try {
      await quotationAPI.deleteDummyShop(slotIdx + 1);
      setShops(prev => prev.map((s, i) => i === slotIdx ? null : s));
      const defaults = ['classic', 'modern', 'minimal'];
      setForms(prev => prev.map((f, i) => i === slotIdx ? { ...EMPTY_DUMMY_SHOP_FORM, design: defaults[i] } : f));
    } catch { /* ignore */ }
  };

  const handleLogoUpload = async (slotIdx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Image too large. Max 2 MB.'); e.target.value = ''; return; }
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) { alert('Use PNG, JPG, GIF or WebP.'); e.target.value = ''; return; }
    setLogoUploading(prev => prev.map((v, i) => i === slotIdx ? true : v));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await quotationAPI.uploadDummyShopLogo(slotIdx + 1, fd);
      setField(slotIdx, 'logo_url', res.data.logo_url);
      setShops(prev => prev.map((s, i) => i === slotIdx && s ? { ...s, logo_url: res.data.logo_url } : s));
      setMsg(prev => prev.map((m, i) => i === slotIdx ? '✅ Logo uploaded!' : m));
      const tid = setTimeout(() => setMsg(prev => prev.map((m, i) => i === slotIdx ? '' : m)), 3000);
      msgTimers.current.push(tid);
    } catch (err) { setMsg(prev => prev.map((m, i) => i === slotIdx ? '❌ Logo upload failed' : m)); }
    setLogoUploading(prev => prev.map((v, i) => i === slotIdx ? false : v));
    e.target.value = '';
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, ...muted }}>Loading…</div>;

  const designLabelColor = { classic: '#3b82f6', modern: '#0d9488', minimal: '#7c3aed' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ ...pg, fontSize: 18, fontWeight: 800, margin: 0 }}>🏭 Dummy Shop Identities</h2>
        <p style={{ ...muted, margin: '4px 0 0', fontSize: 13 }}>Create up to 3 alternate shop identities for generating tender-purpose quotations with different designs</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
        {[0, 1, 2].map(slotIdx => {
          const f = forms[slotIdx];
          const isSet = !!shops[slotIdx];
          const designBg = { classic: '#eff6ff', modern: '#f0fdfa', minimal: '#f5f3ff' };
          return (
            <div key={slotIdx} style={{ ...card, padding: 18, position: 'relative' }}>
              {/* Slot header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span style={{ ...pg, fontWeight: 800, fontSize: 15 }}>Slot {slotIdx + 1}</span>
                  {isSet && <span style={{ marginLeft: 8, fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>SAVED</span>}
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: designBg[f.design] || '#f1f5f9', color: designLabelColor[f.design] || '#374151' }}>{f.design}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Logo upload */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                    {f.logo_url ? (
                      <img src={resolveMediaUrl(f.logo_url)} alt="Logo" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: `2px solid ${acc}40` }} />
                    ) : (
                      <div style={{ width: 56, height: 56, borderRadius: 10, background: darkMode ? '#0F172A' : '#f1f5f9', border: `2px dashed ${darkMode ? '#475569' : '#cbd5e1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: darkMode ? '#475569' : '#94a3b8' }}>🏪</div>
                    )}
                    <label onClick={() => logoInputRefs.current[slotIdx]?.click()} style={{ position: 'absolute', bottom: -4, right: -4, background: acc, color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>✎</label>
                    <input ref={el => logoInputRefs.current[slotIdx] = el} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={e => handleLogoUpload(slotIdx, e)} />
                    {logoUploading[slotIdx] && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10 }}>⏳</div>}
                  </div>
                  <div style={{ flex: 1 }}><label style={lbl}>Shop Name *</label><input style={inp} value={f.shop_name} onChange={e => setField(slotIdx, 'shop_name', e.target.value)} placeholder="Alternate shop name" /></div>
                </div>
                <div><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight: 48, resize: 'vertical' }} value={f.address} onChange={e => setField(slotIdx, 'address', e.target.value)} placeholder="Shop address" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={lbl}>Phone</label><input style={inp} value={f.phone} onChange={e => setField(slotIdx, 'phone', e.target.value)} placeholder="Phone number" /></div>
                  <div><label style={lbl}>GSTIN</label><input style={inp} value={f.gstin} onChange={e => setField(slotIdx, 'gstin', e.target.value)} placeholder="GST number" /></div>
                </div>
                <div><label style={lbl}>Email</label><input style={inp} type="email" value={f.email} onChange={e => setField(slotIdx, 'email', e.target.value)} placeholder="Email address" /></div>
                <div>
                  <label style={lbl}>Print Design</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {DESIGNS.map(d => (
                      <button key={d.value} type="button" onClick={() => setField(slotIdx, 'design', d.value)}
                        style={{ flex: 1, padding: '6px 4px', borderRadius: 7, border: `2px solid ${f.design === d.value ? designLabelColor[d.value] : (darkMode ? '#334155' : '#CBD5E1')}`, background: f.design === d.value ? designBg[d.value] : (darkMode ? '#0F172A' : '#F8FAFC'), cursor: 'pointer', fontSize: 10, fontWeight: f.design === d.value ? 700 : 400, color: f.design === d.value ? designLabelColor[d.value] : (darkMode ? '#64748B' : '#6b7280'), textAlign: 'center', lineHeight: 1.4 }}>
                        {d.label}<br /><span style={{ fontWeight: 400 }}>{d.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {msg[slotIdx] && <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: msg[slotIdx].startsWith('✅') ? '#15803d' : '#dc2626' }}>{msg[slotIdx]}</div>}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={() => save(slotIdx)} disabled={saving[slotIdx]}
                  style={{ flex: 1, padding: '8px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving[slotIdx] ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                  {saving[slotIdx] ? '⏳ Saving…' : '💾 Save Slot ' + (slotIdx + 1)}
                </button>
                {isSet && (
                  <button onClick={() => clear(slotIdx)}
                    style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${darkMode ? '#475569' : '#fecaca'}`, color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 18, padding: '12px 16px', background: darkMode ? '#0F172A' : '#fefce8', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#fde68a'}`, fontSize: 12, color: darkMode ? '#94A3B8' : '#78350f' }}>
        💡 <strong>How to use:</strong> Save your 3 dummy shop details here. When printing a quotation, click <strong>"Generate Tender Set"</strong> to preview and print 3 quotations with different shop identities and designs — perfect for tender submissions.
      </div>
    </div>
  );
};

/* ─── Create / Edit Quotation Form ─────────────────────────────────── */
const CreateQuotation = ({ onSuccess, darkMode, color, editInvoice = null }) => {
  const isEdit = !!editInvoice;
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const [mobile, setMobile] = useState(window.innerWidth < 820);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 820);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [billType, setBillType] = useState(editInvoice?.bill_type || 'quotation');
  const [allCustomers, setAllCustomers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [walkinName, setWalkinName] = useState(editInvoice?.customer_name || '');
  const [walkinPhone, setWalkinPhone] = useState(editInvoice?.customer_phone || '');
  const [walkinAddress, setWalkinAddress] = useState(editInvoice?.customer_address || '');
  const [cart, setCart] = useState(() => {
    if (editInvoice?.items && Array.isArray(editInvoice.items)) {
      const isGSTInit = false;
      return editInvoice.items.map(i => calcItem({ ...i, key: Date.now() + Math.random() }, isGSTInit));
    }
    return [];
  });
  const [notes, setNotes] = useState(editInvoice?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showCustDD, setShowCustDD] = useState(false);
  const [showProdDD, setShowProdDD] = useState(false);
  // BillSoft additional quotation fields
  const [walkinGstin, setWalkinGstin] = useState(editInvoice?.customer_gstin || '');
  const [customerState, setCustomerState] = useState(editInvoice?.customer_state || '');
  const [discountAmount, setDiscountAmount] = useState(editInvoice?.discount_amount ?? '');
  const [shippingAmount, setShippingAmount] = useState(editInvoice?.shipping_amount ?? '');
  const [roundOff, setRoundOff] = useState(editInvoice?.round_off ?? '');
  const [deliveryTerms, setDeliveryTerms] = useState(editInvoice?.delivery_terms || '');
  const [validTill, setValidTill] = useState(editInvoice?.valid_till ? editInvoice.valid_till.split('T')[0] : '');
  const [docReference, setDocReference] = useState(editInvoice?.doc_reference || '');
  const [docReferenceNo, setDocReferenceNo] = useState(editInvoice?.doc_reference_no || '');
  const [taxMethod, setTaxMethod] = useState(editInvoice?.tax_method || 'exclusive');
  const [shippingTaxRate, setShippingTaxRate] = useState(editInvoice?.shipping_tax_rate ?? '');
  const [shippingCessRate, setShippingCessRate] = useState(editInvoice?.shipping_cess_rate ?? '');
  const [isRecurring, setIsRecurring] = useState(editInvoice?.is_recurring ?? false);
  const [dueType, setDueType] = useState(editInvoice?.due_type || '');
  const [dueFreq, setDueFreq] = useState(editInvoice?.due_freq || '');
  const [recurringDate, setRecurringDate] = useState(editInvoice?.recurring_date ? editInvoice.recurring_date.split('T')[0] : '');
  const [recurringStartDate, setRecurringStartDate] = useState(editInvoice?.recurring_start_date ? editInvoice.recurring_start_date.split('T')[0] : '');
  const [recurringExpiryDate, setRecurringExpiryDate] = useState(editInvoice?.recurring_expiry_date ? editInvoice.recurring_expiry_date.split('T')[0] : '');
  const [recurringCycle, setRecurringCycle] = useState(editInvoice?.recurring_cycle ?? '');
  const [recurringNeverExpires, setRecurringNeverExpires] = useState(editInvoice?.recurring_never_expires ?? false);
  const prodRef = useRef(null);
  const custRef = useRef(null);
  const isGST = false;

  /* Load all customers and products on mount for dropdown */
  useEffect(() => {
    customerAPI.list({ limit: 500 }).then(r => {
      const list = r.data.customers || [];
      setAllCustomers(list);
      // In edit mode, try to pre-select customer by ID
      if (editInvoice?.customer_id) {
        const match = list.find(c => c.id === editInvoice.customer_id);
        if (match) setSelectedCustomer(match);
      }
    }).catch(() => {});
    productAPI.list({ limit: 500 }).then(r => {
      setAllProducts(r.data.products || []);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Filter customers by search */
  useEffect(() => {
    if (customerSearch.trim().length >= 1) {
      const q = customerSearch.toLowerCase();
      const filtered = allCustomers.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.mobile || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      );
      setCustomers(filtered.slice(0, 10));
      setShowCustDD(filtered.length > 0);
    } else {
      setCustomers(allCustomers.slice(0, 10));
      setShowCustDD(false);
    }
  }, [customerSearch, allCustomers]);

  /* Filter products by search */
  useEffect(() => {
    if (productSearch.trim().length >= 1) {
      const q = productSearch.toLowerCase();
      const filtered = allProducts.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.barcode || '').includes(q)
      );
      setProducts(filtered.slice(0, 15));
      setShowProdDD(true);
    } else {
      setProducts([]);
      setShowProdDD(false);
    }
  }, [productSearch, allProducts]);

  useEffect(() => {
    const h = e => {
      if (prodRef.current && !prodRef.current.contains(e.target)) setShowProdDD(false);
      if (custRef.current && !custRef.current.contains(e.target)) setShowCustDD(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const addToCart = p => {
    const price = parseFloat(p.selling_price) || 0;
    const existing = cart.find(i => i.product_id === p.id);
    if (existing) { updateItem(existing.key, 'quantity', (Number(existing.quantity) || 0) + 1); }
    else {
      setCart(prev => [...prev, calcItem({ key: Date.now(), product_id: p.id, product_name: p.name, description: '', hsn_sac: p.hsn_sac || '', unit: p.unit || 'Pcs', quantity: 1, price, discount_percent: 0, gst_percent: 0 }, isGST)]);
    }
    setProductSearch(''); setShowProdDD(false);
  };
  const addManualItem = () => setCart(prev => [...prev, calcItem({ key: Date.now(), product_id: null, product_name: '', description: '', hsn_sac: '', unit: 'Pcs', quantity: 1, price: 0, discount_percent: 0, gst_percent: 0 }, isGST)]);
  const updateItem = (key, field, val) => setCart(prev => prev.map(item => item.key !== key ? item : calcItem({ ...item, [field]: val }, isGST)));
  const removeItem = key => setCart(prev => prev.filter(i => i.key !== key));

  const grossTotal = cart.reduce((s, i) => s + n(i.price) * n(i.quantity), 0);
  const totalDiscount = cart.reduce((s, i) => s + (n(i._discAmt) || 0), 0);
  const taxableAmt = grossTotal - totalDiscount;
  const grandTotal = taxableAmt;

  const handleSubmit = async e => {
    e.preventDefault();
    if (cart.length === 0) { alert('Add at least one item'); return; }
    if (cart.some(i => !i.product_name.trim())) { alert('All items must have a name'); return; }
    if (!selectedCustomer && !walkinName.trim()) { alert('Please select a customer or enter a name'); return; }
    setSaving(true);
    try {
      const custName = selectedCustomer ? selectedCustomer.name : (walkinName.trim() || 'Walk-in Customer');
      const custPhone = selectedCustomer ? (selectedCustomer.mobile || null) : (walkinPhone.trim() || null);
      const custAddr = selectedCustomer ? (selectedCustomer.address || null) : (walkinAddress.trim() || null);
      const payload = {
        customer_id: selectedCustomer?.id || editInvoice?.customer_id || null,
        customer_name: custName,
        customer_phone: custPhone,
        customer_address: custAddr,
        customer_gstin: selectedCustomer?.gstin || walkinGstin || null,
        customer_state: customerState || null,
        bill_type: billType,
        items: cart.map(i => ({ product_id: i.product_id, product_name: i.product_name, description: i.description || null, hsn_sac: i.hsn_sac || null, unit: i.unit || 'Pcs', quantity: Number(i.quantity) || 1, price: n(i.price), discount_percent: n(i.discount_percent), gst_percent: 0, total: n(i.total), cgst_rate: 0, sgst_rate: 0, igst_rate: 0, cess_rate: 0 })),
        subtotal: parseFloat(taxableAmt.toFixed(2)),
        gst_total: 0,
        grand_total: parseFloat(grandTotal.toFixed(2)),
        discount_amount: parseFloat(discountAmount) || 0,
        shipping_amount: parseFloat(shippingAmount) || 0,
        cgst_amount: 0, sgst_amount: 0, igst_amount: 0, cess_amount: 0,
        round_off: parseFloat(roundOff) || 0,
        delivery_terms: deliveryTerms || null,
        valid_till: validTill || null,
        doc_reference: docReference || null,
        doc_reference_no: docReferenceNo || null,
        tax_method: taxMethod || 'exclusive',
        shipping_tax_rate: parseFloat(shippingTaxRate) || 0,
        shipping_cess_rate: parseFloat(shippingCessRate) || 0,
        is_recurring: isRecurring,
        due_type: dueType || null,
        due_freq: dueFreq || null,
        recurring_date: recurringDate || null,
        recurring_start_date: recurringStartDate || null,
        recurring_expiry_date: recurringExpiryDate || null,
        recurring_cycle: parseInt(recurringCycle) || 0,
        recurring_never_expires: recurringNeverExpires,
        payment_mode: 'Cash',
        payment_status: 'unpaid',
        notes: notes.trim() || null,
      };
      if (isEdit) { await quotationAPI.fullUpdate(editInvoice.id, payload); onSuccess(null); }
      else { const res = await quotationAPI.create(payload); onSuccess(res.data.id); }
    } catch (err) { alert(err.response?.data?.detail || (isEdit ? 'Failed to update' : 'Failed to create quotation')); }
    setSaving(false);
  };

  const SectionTitle = ({ children }) => <div style={{ ...pg, fontWeight: 700, fontSize: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>;
  const tblInp = (extra = {}) => ({ ...inp, padding: '5px 7px', border: 'none', background: 'transparent', width: '100%', ...extra });
  const tblCell = { padding: '6px 4px', verticalAlign: 'middle', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` };

  return (
    <form onSubmit={handleSubmit}>
      {/* Document-style page wrapper */}
      <div style={{ background: darkMode ? '#0F172A' : '#E2E8F0', padding: '24px 0', minHeight: '100%' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', background: darkMode ? '#1E293B' : '#fff', borderRadius: 14, border: `2px solid ${darkMode ? '#334155' : '#CBD5E1'}`, boxShadow: darkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.08)', padding: 'clamp(12px, 3vw, 28px)', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
      {/* Quote Type */}
      <div style={{ ...card, padding: 16, marginBottom: 16 }}>
        <SectionTitle>📋 Quotation Type</SectionTitle>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {QUOTE_TYPES.map(qt => (
            <button key={qt.value} type="button" onClick={() => setBillType(qt.value)}
              style={{ flex: 1, minWidth: 140, padding: '9px 10px', border: `2px solid ${billType === qt.value ? acc : (darkMode ? '#334155' : '#CBD5E1')}`, borderRadius: 9, background: billType === qt.value ? (darkMode ? '#1e3a5f' : '#eff6ff') : (darkMode ? '#0F172A' : '#F8FAFC'), cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: billType === qt.value ? acc : (darkMode ? '#94A3B8' : '#64748B') }}>{qt.icon} {qt.label}</div>
              <div style={{ fontSize: 10, ...muted, marginTop: 1 }}>{qt.desc}</div>
            </button>
          ))}
        </div>
        <div style={{ marginTop: 10, padding: '8px 12px', background: darkMode ? '#0F172A' : '#f0fdf4', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#bbf7d0'}`, fontSize: 11, color: darkMode ? '#4ade80' : '#15803d' }}>
          💡 Quotations are for price estimates and tender submissions. They do not affect your customer outstanding balance.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 300px', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Customer */}
          <div style={{ ...card, padding: mobile ? 12 : 16 }}>
            <SectionTitle>👤 Customer / Client</SectionTitle>
            {selectedCustomer ? (
              <div style={{ padding: '10px 14px', background: darkMode ? '#0F172A' : '#eff6ff', borderRadius: 8, border: `1px solid ${acc}40`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, ...pg, fontSize: 14, wordBreak: 'break-word' }}>{selectedCustomer.name}</div>
                  {selectedCustomer.mobile && <div style={{ fontSize: 12, ...muted }}>📞 {selectedCustomer.mobile}</div>}
                  {selectedCustomer.address && <div style={{ fontSize: 12, ...muted, wordBreak: 'break-word' }}>📍 {selectedCustomer.address}</div>}
                </div>
                <button type="button" onClick={() => setSelectedCustomer(null)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>×</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div ref={custRef} style={{ position: 'relative' }}>
                  <input style={inp} value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    onFocus={() => { if (allCustomers.length > 0) { setCustomers(customerSearch ? customers : allCustomers.slice(0, 10)); setShowCustDD(true); } }}
                    placeholder="🔍 Search or select customer…" />
                  {showCustDD && customers.length > 0 && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 220, overflowY: 'auto' }}>
                      {customers.map(c => (
                        <button key={c.id} type="button" onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setShowCustDD(false); setWalkinName(c.name || ''); setWalkinPhone(c.mobile || ''); setWalkinAddress(c.address || ''); }}
                          style={{ width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, ...pg, fontSize: 13 }}>
                          <strong style={{ wordBreak: 'break-word' }}>{c.name}</strong><span style={{ marginLeft: 8, fontSize: 11, ...muted }}>{c.mobile || ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div><label style={lbl}>Name<span style={req}>*</span></label><input style={inp} value={walkinName} onChange={e => setWalkinName(e.target.value)} placeholder="Customer / Company name" /></div>
                  <div><label style={lbl}>Phone</label><input style={inp} value={walkinPhone} onChange={e => setWalkinPhone(e.target.value)} placeholder="+91 99999 99999" /></div>
                </div>
                <div><label style={lbl}>Address (optional)</label><input style={inp} value={walkinAddress} onChange={e => setWalkinAddress(e.target.value)} placeholder="Street, City, Pincode" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div><label style={lbl}>GSTIN</label><input style={inp} value={walkinGstin} onChange={e => setWalkinGstin(e.target.value)} placeholder="27AABCU9603R1ZX" maxLength={15} /></div>
                  <div><label style={lbl}>State</label><input style={inp} value={customerState} onChange={e => setCustomerState(e.target.value)} placeholder="Maharashtra" /></div>
                </div>
              </div>
            )}
          </div>

          {/* Quotation Details */}
          <div style={{ ...card, padding: mobile ? 12 : 16 }}>
            <SectionTitle>📋 Quotation Details</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
              <div><label style={lbl}>Valid Till</label><StrictDateInput style={inp} value={validTill} onChange={e => setValidTill(e.target.value)} /></div>
              <div><label style={lbl}>Tax Method</label><select style={inp} value={taxMethod} onChange={e => setTaxMethod(e.target.value)}><option value="exclusive">Exclusive</option><option value="inclusive">Inclusive</option></select></div>
              <div><label style={lbl}>Delivery Terms</label><input style={inp} value={deliveryTerms} onChange={e => setDeliveryTerms(e.target.value)} placeholder="FOB, CIF, etc." /></div>
              <div><label style={lbl}>Doc Reference</label><input style={inp} value={docReference} onChange={e => setDocReference(e.target.value)} placeholder="Enquiry / RFQ" /></div>
              <div><label style={lbl}>Doc Reference No.</label><input style={inp} value={docReferenceNo} onChange={e => setDocReferenceNo(e.target.value)} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10, padding: '10px 0', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
              <div><label style={lbl}>Discount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)} placeholder="0.00" /></div>
              <div><label style={lbl}>Shipping (₹)</label><input style={inp} type="number" min="0" step="0.01" value={shippingAmount} onChange={e => setShippingAmount(e.target.value)} placeholder="0.00" /></div>
              <div><label style={lbl}>Shipping Tax %</label><input style={inp} type="number" min="0" step="0.01" value={shippingTaxRate} onChange={e => setShippingTaxRate(e.target.value)} placeholder="0" /></div>
              <div><label style={lbl}>Shipping Cess %</label><input style={inp} type="number" min="0" step="0.01" value={shippingCessRate} onChange={e => setShippingCessRate(e.target.value)} placeholder="0" /></div>
              <div><label style={lbl}>Round Off (₹)</label><input style={inp} type="number" step="0.01" value={roundOff} onChange={e => setRoundOff(e.target.value)} placeholder="0.00" /></div>
            </div>
            {/* Recurring billing section */}
            <div style={{ marginTop: 10, padding: '10px 14px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: darkMode ? '#F1F5F9' : '#0F172A' }}>
                <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} /> 🔄 Recurring Quotation
              </label>
              {isRecurring && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10 }}>
                  <div><label style={lbl}>Due Type</label><input style={inp} value={dueType} onChange={e => setDueType(e.target.value)} placeholder="On Receipt / Net 30" /></div>
                  <div><label style={lbl}>Frequency</label><input style={inp} value={dueFreq} onChange={e => setDueFreq(e.target.value)} placeholder="Monthly / Quarterly" /></div>
                  <div><label style={lbl}>Recurring Date</label><StrictDateInput style={inp} value={recurringDate} onChange={e => setRecurringDate(e.target.value)} /></div>
                  <div><label style={lbl}>Start Date</label><StrictDateInput style={inp} value={recurringStartDate} onChange={e => setRecurringStartDate(e.target.value)} /></div>
                  <div><label style={lbl}>Expiry Date</label><StrictDateInput style={inp} value={recurringExpiryDate} onChange={e => setRecurringExpiryDate(e.target.value)} /></div>
                  <div><label style={lbl}>Cycle Count</label><input style={inp} type="number" min="0" value={recurringCycle} onChange={e => setRecurringCycle(e.target.value)} /></div>
                  <div style={{ display: 'flex', alignItems: 'center', paddingTop: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>
                      <input type="checkbox" checked={recurringNeverExpires} onChange={e => setRecurringNeverExpires(e.target.checked)} /> Never Expires
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div style={{ ...card, padding: mobile ? 12 : 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <SectionTitle>📦 Items</SectionTitle>
              <button type="button" onClick={addManualItem} style={{ padding: '5px 12px', border: `1px dashed ${acc}`, borderRadius: 7, background: 'transparent', color: acc, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ Manual Item</button>
            </div>

            <div ref={prodRef} style={{ position: 'relative', marginBottom: 12 }}>
              <input style={{ ...inp, paddingLeft: 36 }} value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                onFocus={() => { if (allProducts.length > 0 && !productSearch) { setProducts(allProducts.slice(0, 15)); setShowProdDD(true); } }}
                placeholder="🔍 Search or select product…" />
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', ...muted, fontSize: 14 }}>🔍</span>
              {showProdDD && products.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 280, overflowY: 'auto' }}>
                  {products.map(p => (
                    <button key={p.id} type="button" onClick={() => addToCart(p)}
                      style={{ width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...pg }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, wordBreak: 'break-word' }}>{p.name}</div>
                        <div style={{ fontSize: 10, ...muted }}>{p.sku ? `SKU: ${p.sku}` : ''}{p.unit ? ' · ' + p.unit : ''}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>₹{p.selling_price}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', ...muted, fontSize: 13, border: `1.5px dashed ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 8 }}>
                Search for a product above or click <strong style={{ color: acc }}>+ Manual Item</strong>
              </div>
            ) : mobile ? (
              /* ─── Mobile Card Layout for Items ─── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cart.map((item, idx) => (
                  <div key={item.key} style={{ borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 12, background: darkMode ? '#0F172A' : '#FAFBFC' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <span style={{ ...muted, fontSize: 11, fontWeight: 700 }}>#{idx+1}</span>
                      <button type="button" onClick={() => removeItem(item.key)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 18, cursor: 'pointer', padding: 0 }}>×</button>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={lbl}>Item Name</label>
                      <input value={item.product_name} onChange={e => updateItem(item.key, 'product_name', e.target.value)} placeholder="Item name"
                        style={{ ...inp, fontWeight: 600 }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={lbl}>Description</label>
                      <input value={item.description || ''} onChange={e => updateItem(item.key, 'description', e.target.value)} placeholder="Optional"
                        style={{ ...inp, fontSize: 12 }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={lbl}>Unit</label>
                        <select value={item.unit} onChange={e => updateItem(item.key, 'unit', e.target.value)}
                          style={{ ...inp, fontSize: 12 }}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Qty</label>
                        <input type="number" min="0.01" step="0.01" value={item.quantity}
                          onChange={e => updateItem(item.key, 'quantity', e.target.value)}
                          style={{ ...inp, textAlign: 'right', fontSize: 12 }} />
                      </div>
                      <div>
                        <label style={lbl}>Rate (₹)</label>
                        <input type="number" min="0" step="0.01" value={item.price}
                          onChange={e => updateItem(item.key, 'price', e.target.value)}
                          style={{ ...inp, textAlign: 'right', fontSize: 12 }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={lbl}>Discount %</label>
                        <input type="number" min="0" max="100" step="0.5" value={item.discount_percent}
                          onChange={e => updateItem(item.key, 'discount_percent', e.target.value)}
                          style={{ ...inp, textAlign: 'right', fontSize: 12, color: n(item.discount_percent) > 0 ? '#d97706' : undefined }} />
                      </div>
                      <div>
                        <label style={lbl}>Total</label>
                        <div style={{ ...inp, background: darkMode ? '#1E293B' : '#f0fdf4', fontWeight: 700, color: '#16a34a', textAlign: 'right' }}>₹{fmtNum(n(item.total))}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* ─── Desktop Table Layout for Items ─── */
              <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 580 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['#', 'Item Name', 'Unit', 'Qty', 'Rate (₹)', 'Disc%', 'Total', ''].map(h => (
                        <th key={h} style={{ padding: '8px 6px', textAlign: h === 'Item Name' ? 'left' : 'right', fontSize: 10, fontWeight: 700, ...muted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, idx) => (
                      <tr key={item.key} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ ...tblCell, paddingLeft: 8, ...muted, fontSize: 11, width: 24, textAlign: 'right' }}>{idx+1}</td>
                        <td style={{ ...tblCell, minWidth: 140 }}>
                          <input value={item.product_name} onChange={e => updateItem(item.key, 'product_name', e.target.value)} placeholder="Item name"
                            style={{ ...tblInp(), fontWeight: 600, ...pg, marginBottom: 2 }} />
                          <input value={item.description || ''} onChange={e => updateItem(item.key, 'description', e.target.value)} placeholder="Description (optional)"
                            style={{ ...tblInp(), fontSize: 10, ...muted }} />
                        </td>
                        <td style={{ ...tblCell, width: 62 }}>
                          <select value={item.unit} onChange={e => updateItem(item.key, 'unit', e.target.value)}
                            style={{ ...inp, padding: '4px 4px', fontSize: 11, width: '100%' }}>
                            {UNITS.map(u => <option key={u}>{u}</option>)}
                          </select>
                        </td>
                        <td style={{ ...tblCell, width: 52 }}>
                          <input type="number" min="0.01" step="0.01" value={item.quantity} onChange={e => updateItem(item.key, 'quantity', e.target.value)}
                            style={tblInp({ textAlign: 'right', width: 46 })} />
                        </td>
                        <td style={{ ...tblCell, width: 80 }}>
                          <input type="number" min="0" step="0.01" value={item.price} onChange={e => updateItem(item.key, 'price', e.target.value)}
                            style={tblInp({ textAlign: 'right' })} />
                        </td>
                        <td style={{ ...tblCell, width: 52 }}>
                          <input type="number" min="0" max="100" step="0.5" value={item.discount_percent} onChange={e => updateItem(item.key, 'discount_percent', e.target.value)}
                            style={tblInp({ textAlign: 'right', color: n(item.discount_percent) > 0 ? '#d97706' : undefined })} />
                        </td>
                        <td style={{ ...tblCell, width: 80, textAlign: 'right', fontWeight: 700, color: '#16a34a', paddingRight: 6 }}>₹{fmtNum(n(item.total))}</td>
                        <td style={{ ...tblCell, width: 28, paddingRight: 6, textAlign: 'center' }}>
                          <button type="button" onClick={() => removeItem(item.key)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer' }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: mobile ? 'static' : 'sticky', top: 72 }}>
          <div style={{ ...card, padding: 16 }}>
            <div style={{ ...pg, fontWeight: 700, fontSize: 14, marginBottom: 12 }}>📝 Notes</div>
            <textarea style={{ ...inp, minHeight: 62, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Remarks, validity, delivery terms…" />
          </div>

          <div style={{ ...card, padding: 16 }}>
            <div style={{ ...pg, fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🧮 Summary</div>
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span style={muted}>Gross Amount</span><span style={pg}>₹{fmtNum(grossTotal)}</span></div>
              {totalDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span style={{ color: '#d97706' }}>Less: Discount</span><span style={{ color: '#d97706', fontWeight: 600 }}>-₹{fmtNum(totalDiscount)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0 4px', borderTop: `2px solid ${darkMode ? '#475569' : '#CBD5E1'}`, marginTop: 4, fontWeight: 800, fontSize: 16 }}>
                <span style={pg}>Quote Total</span><span style={{ color: '#16a34a' }}>₹{fmtNum(grandTotal)}</span>
              </div>
              {cart.length > 0 && <div style={{ fontSize: 11, ...muted, marginTop: 3 }}>{cart.length} item{cart.length > 1 ? 's' : ''}</div>}
            </div>
            <button type="submit" disabled={saving || cart.length === 0}
              style={{ width: '100%', marginTop: 16, padding: 12, background: saving || cart.length === 0 ? '#94a3b8' : acc, color: '#fff', border: 'none', borderRadius: 9, fontWeight: 800, fontSize: 14, cursor: saving || cart.length === 0 ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳ Saving…' : isEdit ? '💾 Update Quotation' : '📋 Save & Print Quotation'}
            </button>
          </div>
        </div>
      </div>
      </div>
      </div>
    </form>
  );
};

/* ─── Main Quotations Page ──────────────────────────────────────────── */
const Quotations = () => {
  const { darkMode, color } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceRoute = useRef(searchParams.get('source') || null);
  const [tab, setTab] = useState(searchParams.get('action') === 'create' ? 'create' : 'list');
  const [printState, setPrintState] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [dummyShops, setDummyShops] = useState([]);
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  // Clean up query params
  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('source')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action'); next.delete('source');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    quotationAPI.listDummyShops().then(r => setDummyShops(r.data || [])).catch(() => {});
  }, [tab]); // refresh when switching to dummy shops tab

  // Listen for sv-open-modal custom event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'new-quotation') setTab('create');
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  // ESC key to go back from create/edit to list, or return to source page
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT' || e.target.isContentEditable)) return;
      if (printState) { setPrintState(null); return; }
      if (tab === 'create' || tab === 'edit') { setEditingInvoice(null); setTab('list'); return; }
      if (tab === 'list' && sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tab, printState, navigate]);

  const openPrint = async (inv) => {
    try {
      const [invRes, shopRes] = await Promise.all([quotationAPI.get(typeof inv === 'string' ? inv : inv.id), settingsAPI.get()]);
      setPrintState({ invoice: invRes.data, shop: shopRes.data });
    } catch { alert('Failed to load quotation for printing'); }
  };

  const openEdit = async (inv) => {
    try {
      const invId = typeof inv === 'string' ? inv : inv.id;
      if (!invId) { alert('Invalid quotation ID'); return; }
      const res = await quotationAPI.get(invId);
      if (!res.data) { alert('Quotation data is empty'); return; }
      setEditingInvoice(res.data);
      setTab('edit');
    } catch (err) {
      console.error('Edit load error:', err);
      const msg = err.response?.data?.detail || err.message || 'Unknown error';
      alert('Failed to load quotation: ' + msg);
    }
  };

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(4px, 2vw, 0px)' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ ...pg, fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: 800, margin: 0 }}>📋 Quotations & Estimates</h1>
          <p style={{ ...muted, margin: '3px 0 0', fontSize: 12 }}>Create price estimates and tender-purpose quotations</p>
        </div>
        <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 16, display: 'flex', gap: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {[
            ['list',    '📋 Quotation List'],
            ['create',  '➕ New Quotation'],
            ['shops',   '🏭 Dummy Shops'],
            ...(tab === 'edit' ? [['edit', '✏️ Edit']] : []),
          ].map(([id, label]) => (
            <button key={id} onClick={() => { if (id !== 'edit') setEditingInvoice(null); setTab(id); }}
              style={{ padding: '8px 14px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, background: 'transparent', borderBottom: tab === id ? `3px solid ${acc}` : '3px solid transparent', color: tab === id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2, transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'list' && (
          <QuotationList onCreateNew={() => setTab('create')} onPrint={openPrint} onEdit={openEdit} darkMode={darkMode} color={color} />
        )}
        {tab === 'create' && (
          <CreateQuotation
            onSuccess={id => { setTab('list'); if (id) setTimeout(() => openPrint(id), 500); }}
            darkMode={darkMode} color={color}
          />
        )}
        {tab === 'shops' && (
          <DummyShopsManager darkMode={darkMode} color={color} />
        )}
        {tab === 'edit' && editingInvoice && (
          <CreateQuotation
            key={editingInvoice.id}
            editInvoice={editingInvoice}
            onSuccess={() => { setEditingInvoice(null); setTab('list'); }}
            darkMode={darkMode} color={color}
          />
        )}
      </div>

      {printState && (
        <QuotePrintModal
          invoice={printState.invoice}
          shop={printState.shop}
          dummyShops={dummyShops}
          onClose={() => setPrintState(null)}
        />
      )}
    </Layout>
  );
};

export default Quotations;
